/**
 * CadHistoryPage
 * @module pages/CadHistoryPage
 */

import { useContext, useMemo, useState } from 'react';
import { LocalizationContext } from 'context/localization.context';
import ContentArea from 'components/framework/ContentArea';
import SectionHeader from 'components/ui/SectionHeader';
import Layout from 'components/ui/Layout';
import Breadcrumbs from 'components/framework/Breadcrumbs';

import Pager from 'components/ui/Pager';
import { useTable, useSortBy, usePagination } from 'react-table';
import {
	handleSortByKeyDown,
	handleKeyboardNavigation,
} from 'components/ui/Table';

import { getURLParameter, scrollToTop } from 'common/helpers';
import { useMedia } from 'hooks';
import Icon from 'components/ui/Icon';
import { CadHistoryPageModel } from 'types/page-types';
import { CellButtonLink } from 'components/ui/Table/table.styles';
import CadModal from 'components/ui/Cadmodal';

const CadHistoryPage: React.FC<CadHistoryPageModel> = ({
	breadcrumbs,
	heading,
	text,
	lines,
	isInEditMode,
	contentArea,
	framework,
}) => {
	const { t }: any = useContext(LocalizationContext);
	const isMobile = useMedia('(max-width: 490px)');
	const [linesState, setLinesState] = useState(lines);
	const currentPage = parseInt(getURLParameter('page') || '1');
	const [cadModalOpen, setCadModalOpen] = useState(false);
	const [cadLink, setCadLink] = useState<string>('');

	const data = useMemo(() => linesState, [linesState]);
	const columns = useMemo(
		() => [
			{ Header: t('cadhistorypage/project'), accessor: 'project' },
			{ Header: t('cadhistorypage/date'), accessor: 'date' },
			{ Header: t('cadhistorypage/subproject'), accessor: 'subProject' },
		],
		//eslint-disable-next-line
		[]
	);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		prepareRow,
		page,
		pageOptions,
		gotoPage,
		state: { pageIndex },
	} = useTable(
		{
			columns,
			data,
			initialState: {
				pageIndex: !isNaN(currentPage) ? currentPage - 1 : 0,
				pageSize: 25,
				sortBy: [
					{
						id: 'date',
						desc: true,
					},
				],
			},
		},
		useSortBy,
		usePagination
	);

	const handleCadModal = (cadLink: string) => {
		if (!cadLink) return;
		setCadLink(cadLink);
		setCadModalOpen(true);
	};
	return (
		<main id="main-content">
			{breadcrumbs && (
				<Layout>
					<Breadcrumbs links={breadcrumbs} />
				</Layout>
			)}
			<SectionHeader
				heading={heading}
				text={text}
				size={'large'}
				isInEditMode={isInEditMode}
			/>
			{/* <Layout> // Not currently in use.
				<OverflowMessage overflow={overflowItem} />
			</Layout> */}
			<Layout>
				<div className="flex flex-col mb-6">
					<div className="w-full">
						<div className="max-w-full pb-6 overflow-y-auto">
							<table
								className="border-spacing-0 w-full"
								{...getTableProps({ role: 'grid' })}
							>
								<caption className="sr-only">{heading}</caption>
								<thead className="bg-blue">
									{headerGroups.map((headerGroup) => (
										<tr
											className="text-white text-2xl p-1"
											{...headerGroup.getHeaderGroupProps()}
										>
											{headerGroup.headers.map((column, i) => (
												<th
													className="px-4 py-2 text-2xl font-normal text-left whitespace-no-wrap"
													{...column.getHeaderProps(
														column.getSortByToggleProps({
															title: `${t('shared/table/sortby')} ${
																column.Header
															}`,
															role: 'button',
															tabIndex: 0,
														})
													)}
													aria-sort={
														column.isSorted && !column.isSortedDesc
															? t('shared/table/ascending')
															: t('shared/table/descending')
													}
													data-header-col={i}
													onKeyDown={(e) => {
														if (column.canSort) {
															return handleSortByKeyDown(
																e,
																column,
																i,
																page.length,
																columns.length
															);
														}
													}}
												>
													{column.render('Header')}
													<div className="relative w-2 inline-block ml-1">
														{column.isSorted && column.isSortedDesc && (
															<Icon icon="sortDescending" />
														)}
														{column.isSorted && !column.isSortedDesc && (
															<Icon icon="sortAscending" />
														)}
													</div>
												</th>
											))}
										</tr>
									))}
								</thead>
								<tbody {...getTableBodyProps()}>
									{page.map((row: any, i: number) => {
										prepareRow(row);
										return (
											<tr
												className="odd:bg-white even:bg-greyLight text-2xl"
												{...row.getRowProps()}
											>
												{row.cells.map((cell: any, index: number) => {
													return (
														<td
															className="px-4 py-2 whitespace-no-wrap"
															{...cell.getCellProps({})}
															onKeyDown={(e) => {
																return handleKeyboardNavigation(
																	e,
																	page.length,
																	columns.length,
																	i
																);
															}}
															data-row={i}
															data-col={index}
														>
															{cell.column.id === 'project' ? (
																<CellButtonLink
																	onClick={() =>
																		handleCadModal(row.original.link)
																	}
																>
																	{cell.value}
																</CellButtonLink>
															) : (
																<span className="text-p py-2 px-4">
																	{cell.render('Cell')}
																</span>
															)}
														</td>
													);
												})}
											</tr>
										);
									})}
								</tbody>
							</table>
							{/* <div className="my-10 text-center w-full overflow-hidden">
								<Icon icon="loader" animate="spin" size={2} />
							</div> */}
						</div>
						{linesState && linesState.length === 0 && (
							<p className="text-center text-2xl my-4">
								{t('cadhistorypage/noresultmessage')}
							</p>
						)}
						{pageOptions && pageOptions.length > 1 && (
							<div className="my-2">
								<Pager
									nrOfPages={pageOptions.length}
									currentPage={pageIndex + 1}
									total={pageOptions.length}
									onPageChange={(value: any) => {
										scrollToTop();
										gotoPage(value - 1);
									}}
									isMobile={isMobile}
								/>
							</div>
						)}
					</div>
				</div>
				{cadModalOpen && cadLink && (
					<CadModal
						api={framework.api}
						apiUrl={cadLink}
						onToggle={() => setCadModalOpen(false)}
					/>
				)}
			</Layout>
			<ContentArea contentArea={contentArea} />
		</main>
	);
};

export default CadHistoryPage;
